import { whenAyReady, withAyManager } from './queue'

/**
 * Write AY custom dimensions once the SDK is up. `build` runs at that point, so it
 * reads state as it is then, not when the write was queued.
 */
export function writeCustomDimensions(
  build: () => Record<string, unknown>
): void {
  whenAyReady(() => {
    const custom = window.assertive?.analytics?.custom
    if (custom) {
      Object.assign(custom, build())
    }
  })
}

/**
 * Writing straight into `window.assertive.analytics.custom` on a consent
 * click can race AY's own refresh-cycle targeting and trip its "Improper use
 * of ad refresh" check. Defer the write to `beforeRefresh` — AY's hook for
 * "before the auctions run", the point it expects targeting changes — so the
 * write lands ahead of the very refresh a consent `allow` triggers. Patches
 * are cleared once applied, so the persistent listener writes exactly once.
 * A function patch is built at that point, so it reads state as it is then.
 */
type CustomPatch = Record<string, unknown> | (() => Record<string, unknown>)

let pendingPatches: CustomPatch[] = []
let beforeRefreshHandlerRegistered = false

const applyPendingPatch = () => {
  const custom = window.assertive?.analytics?.custom
  if (pendingPatches.length === 0 || !custom) return
  const patches = pendingPatches
  pendingPatches = []
  for (const patch of patches) {
    Object.assign(custom, typeof patch === 'function' ? patch() : patch)
  }
}

export const queueCustomPatch = (patch: CustomPatch) => {
  pendingPatches.push(patch)

  if (!beforeRefreshHandlerRegistered) {
    beforeRefreshHandlerRegistered = true
    withAyManager(() => {
      window.ayManagerEnv?.onEvent('beforeRefresh', applyPendingPatch)
    })
  }
}
